import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './components/Auth';
import { usePermissions } from './hooks/usePermissions';
import { useAuth } from './hooks/useAuth';
import LoadingFallback from './components/LoadingFallback';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import analyticsService from './services/analyticsService';
import { useGAPageTracking } from './hooks/useGAPageTracking';

// Import lazy components
import {
  Auth,
  Dashboard,
  PrivateRoute,
  ProtectedRoute,
  OrganizationManagement,
  BackendLayout,
  BackendLandingPage,
  BackendSettings,
  AuctionsPage,
  AuctionHelper,
  DashboardLeftNav,
  MSG91Notifications,
  MSG91Config,
  UserManagement,
  UserCreation,
  UserListPage,
  EditUserPage,
  AllUsersListPage,
  EditAllUserPage,
  AdminUserListPage,
  AdminEditUserPage,
  DealerManagement,
  RoleManagement,
  BDMCityMapping,
  DatabaseManagement,
  DealerCityMapping,
  LandingPage,
  DealerHome,
  ManageDrivers,
  ManageSuppliers,
  FavoritesPage,
  EvaluatorHome,
  OTPVerificationPage,
  OTPGate,
  OTPGateHTML,
  OTPGateBackend,
  YardEvaluationLinkGenerator,
  AdminLeftNav,
  Header,
  Footer,
  MyCars,
  SupplierMaster,
  YardMaster,
  SteeringWheelMaster,
  VehicleSpecMaster,
  BannerMaster,
  OverlayMaster,
  CarTypeMaster,
  FeaturedCarsMaster,
  TypeAngleForm,
  MyCarsV2,
  ErrorBoundary,
  SupplierConverterPage,
  AddInventoryPage,
  BDMCarsV2,
  EvaluationOTP,
  EvaluationYards,
  EvaluationCars,
  EvaluationPhotoUpload,
  EvaluationPage,
  DeliveryPage,
  FinanceApproval,
  FinanceApprover,
  HeadFinanceApprover,
  DepositRefundTracking,
  NOCManagement,
  ManageInventory,
  AnalyticsDashboard,
  AnalyticsTest,
  BatchJobManager,
  QuoteAdmin
} from './components/LazyComponents';
import DealerOnboarding from './components/dealer-onboarding/DealerOnboarding';

// Component that handles GA page tracking
function AppWithGATracking() {
  useGAPageTracking();
  
  return (
            <Routes>
              {/* Login Route */}
              <Route 
                path="/login" 
                element={
                  <Suspense fallback={<LoadingFallback message="Loading login..." />}>
                    <Auth />
                  </Suspense>
                } 
              />
              
              {/* OTP Gate Route - Public Access */}
              <Route 
                path="/backend/otp-gate" 
                element={
                  <Suspense fallback={<LoadingFallback message="Loading OTP gate..." />}>
                    <OTPGateBackend />
                  </Suspense>
                } 
              />
              
              {/* Dashboard Route */}
              <Route
                path="/dashboard"
                element={
                  <DashboardLayout>
                    <Suspense fallback={<LoadingFallback message="Loading dashboard..." />}>
                      <PrivateRoute>
                        <Dashboard />
                      </PrivateRoute>
                    </Suspense>
                  </DashboardLayout>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/*"
                element={
          <Suspense fallback={<LoadingFallback message="Loading admin portal..." />}>
            <ProtectedRoute requiredRole={["admin"]}>
                      <AdminLayout>
                        <Routes>
                  <Route path="/users" element={<UserManagement />} />
                  <Route path="/roles" element={<RoleManagement />} />
                  <Route path="/organizations" element={<OrganizationManagement />} />
                  <Route path="/settings" element={<BackendSettings />} />
                        </Routes>
                      </AdminLayout>
                    </ProtectedRoute>
                  </Suspense>
                }
              />

              {/* Backend Routes */}
              <Route
        path="/backend/*"
                element={
                  <Suspense fallback={<LoadingFallback message="Loading backend..." />}>
            <ProtectedRoute requiredRole={["admin", "orguser", "finance", "bdm", "evaluator", "auction_user", "doc_analyst", "quote_admin"]}>
                      <BackendLayout>
                <Routes>
                  <Route path="/bdm-cars-v2/*" element={<BDMCarsV2 />} />
                  <Route path="/my-cars-v2/*" element={<MyCarsV2 />} />
                  <Route path="/analytics" element={<AnalyticsDashboard />} />
                  <Route path="/analytics-test" element={<AnalyticsTest />} />
                  <Route path="/finance-approval" element={<FinanceApproval />} />
                  <Route path="/finance-approver" element={<FinanceApprover />} />
                  <Route path="/head-finance-approver" element={<HeadFinanceApprover />} />
                  <Route path="/auctions" element={<AuctionsPage />} />
                  <Route path="/auction-helper" element={<AuctionHelper />} />
                  <Route path="/quote-admin" element={<QuoteAdmin />} />
                  <Route path="/settings" element={<BackendSettings />} />
                  <Route path="/msg91-notifications" element={<MSG91Notifications />} />
                  <Route path="/msg91-config" element={<MSG91Config />} />
                  <Route path="/users" element={<UserManagement />} />
                  <Route path="/user-management" element={<UserManagement />} />
                  <Route path="/user-creation" element={<UserCreation />} />
                  <Route path="/user-list" element={<UserListPage />} />
                  <Route path="/edit-user/:userId" element={<EditUserPage />} />
                  <Route path="/all-users-list" element={<AllUsersListPage />} />
                  <Route path="/edit-all-user/:userId" element={<EditAllUserPage />} />
                  <Route path="/admin-user-list" element={<AdminUserListPage />} />
                  <Route path="/edit-admin-user/:userId" element={<AdminEditUserPage />} />
                  <Route path="/dealer-management" element={<DealerOnboarding />} />
                  <Route path="/role-management" element={<RoleManagement />} />
                  <Route path="/bdm-city-mapping" element={<BDMCityMapping />} />
                  <Route path="/database-management" element={<DatabaseManagement />} />
                  <Route path="/dealer-city-mapping" element={<DealerCityMapping />} />
                  <Route path="/manage-drivers" element={<ManageDrivers />} />
                  <Route path="/manage-suppliers" element={<ManageSuppliers />} />
                  <Route path="/favorites" element={<FavoritesPage />} />
                  <Route path="/evaluator-home" element={<EvaluatorHome />} />
                  <Route path="/otp-verification" element={<OTPVerificationPage />} />
                  <Route path="/otp-gate" element={<OTPGate />} />
                  <Route path="/otp-gate-html" element={<OTPGateHTML />} />
                  <Route path="/yard-evaluation-link" element={<YardEvaluationLinkGenerator />} />
                  <Route path="/supplier-master" element={<SupplierMaster />} />
                  <Route path="/yard-master" element={<YardMaster />} />
                  <Route path="/steering-wheel-master" element={<SteeringWheelMaster />} />
                  <Route path="/vehicle-spec-master" element={<VehicleSpecMaster />} />
                  <Route path="/banner-master" element={<BannerMaster />} />
                  <Route path="/overlay-master" element={<OverlayMaster />} />
                  <Route path="/car-type-master" element={<CarTypeMaster />} />
                  <Route path="/featured-cars-master" element={<FeaturedCarsMaster />} />
                  <Route path="/type-angle" element={<TypeAngleForm />} />
                  <Route path="/supplier-converter" element={<SupplierConverterPage />} />
                  <Route path="/add-inventory" element={<AddInventoryPage />} />
                  <Route path="/evaluation-otp" element={<EvaluationOTP />} />
                  <Route path="/evaluation-yards" element={<EvaluationYards />} />
                  <Route path="/evaluation-cars" element={<EvaluationCars />} />
                  <Route path="/evaluation-photo-upload" element={<EvaluationPhotoUpload />} />
                  <Route path="/evaluation" element={<EvaluationPage />} />
                  <Route path="/delivery" element={<DeliveryPage />} />
                  <Route path="/deposit-refund-tracking" element={<DepositRefundTracking />} />
                  <Route path="/noc-management" element={<NOCManagement />} />
                  <Route path="/manage-inventory" element={<ManageInventory />} />
                  <Route path="/batch-job-manager" element={<BatchJobManager />} />
                </Routes>
                      </BackendLayout>
                    </ProtectedRoute>
                  </Suspense>
                } 
              />

              {/* Other Routes */}
              <Route
                path="/manage-suppliers"
                element={<ManageSuppliersProtected />}
              />

              <Route
                path="/yard-evaluation-link"
                element={
                  <Suspense fallback={<LoadingFallback message="Loading backend layout..." />}>
                    <ProtectedRoute requiredRole={["admin", "orguser"]}>
                      <BackendLayout>
                        <YardEvaluationLinkGenerator />
                      </BackendLayout>
                    </ProtectedRoute>
                  </Suspense>
                }
              />

              <Route 
                path="/generate-yard-link" 
                element={
                  <Suspense fallback={<LoadingFallback message="Loading yard link generator..." />}>
                    <YardEvaluationLinkGenerator />
                  </Suspense>
                } 
              />

              <Route
                path="/dealer"
                element={
                  <Suspense fallback={<LoadingFallback message="Loading dealer portal..." />}>
                    <ProtectedRoute requiredRole={["admin", "orguserdealer"]}>
                      <DealerHome />
                    </ProtectedRoute>
                  </Suspense>
                }
              />

              {/* Home Routes */}
              <Route
                path="/home/*"
                element={
                  <Suspense fallback={<LoadingFallback message="Loading home..." />}>
                    <ProtectedRoute requiredRole={["admin", "orguser", "finance", "bdm", "evaluator", "auction_user", "doc_analyst"]}>
                      <BackendLayout>
                        <Routes>
                          <Route path="/my-cars-v2/*" element={<MyCarsV2 />} />
                        </Routes>
                      </BackendLayout>
                    </ProtectedRoute>
                  </Suspense>
                }
              />

              <Route 
                path="/eval/yard/:dealerId" 
                element={
                  <Suspense fallback={<LoadingFallback message="Loading OTP verification..." />}>
                    <OTPVerificationPage />
                  </Suspense>
                } 
              />

              <Route 
                path="/landing" 
                element={
                  <Suspense fallback={<LoadingFallback message="Loading landing page..." />}>
                    <LandingPage />
                  </Suspense>
                } 
              />

              {/* Default Route */}
              <Route path="/" element={<Navigate to="/login" replace />} />
            </Routes>
  );
}

// Layout components with Suspense
function DashboardLayout({ children }) {
  return (
    <div className="dashboard-layout">
      <Suspense fallback={<LoadingFallback message="Loading navigation..." />}>
        <DashboardLeftNav />
      </Suspense>
      <div className="layout-content">
        {children}
      </div>
    </div>
  );
}

function AdminLayout({ children }) {
  return (
    <div className="admin-layout">
      <Suspense fallback={<LoadingFallback message="Loading header..." />}>
        <AdminLeftNav />
      </Suspense>
      <div className="layout-content">
        {children}
      </div>
    </div>
  );
}

// Protected components
const ManageSuppliersProtected = () => {
  const { can } = usePermissions();
  if (!can('manage-suppliers-page')) {
    return <div className="access-denied">Access denied. You don't have permission to manage suppliers.</div>;
  }
  return (
    <Suspense fallback={<LoadingFallback message="Loading suppliers..." />}>
      <ManageSuppliers />
    </Suspense>
  );
};

function App() {
  useEffect(() => {
    // Initialize analytics service
    analyticsService.init();
  }, []);

  return (
    <Suspense fallback={<LoadingFallback message="Initializing application..." />}>
      <ErrorBoundary>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <AppWithGATracking />
          </AuthProvider>
        </Router>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </ErrorBoundary>
    </Suspense>
  );
}

export default App;
